import { FALLBACK_LANGUAGE, findLanguage, listSupportedLanguages } from './languages';

/**
 * The locale segment: a two-letter ISO 639-1 language code, and the first path
 * segment of every application URL (CON-002).
 *
 * Named `locales` rather than `languages` because that is the job the module
 * does — parsing, validating and rewriting the URL segment — and because the
 * day a market axis is genuinely needed, `at-en` parses here and nowhere else
 * has to learn about it.
 */

const LOCALE_PATTERN = /^[a-z]{2}$/u;

export function parseLocale(localeCode) {
    const candidate = String(localeCode || '').toLowerCase();

    if (!LOCALE_PATTERN.test(candidate) || !findLanguage(candidate)) {
        return null;
    }

    return { localeCode: candidate, languageCode: candidate };
}

export function isSupportedLocale(localeCode) {
    return parseLocale(localeCode) !== null;
}

/** Every shippable locale — drives hreflang, the sitemap and the switcher. */
export function listSupportedLocales() {
    return listSupportedLanguages();
}

/** `en` -> `en`. The bridge for `<html lang>`, `hreflang` and `Intl`. */
export function toBcp47(localeCode) {
    return parseLocale(localeCode)?.languageCode ?? null;
}

/**
 * Split a pathname into its locale segment and the route beneath it.
 * An unprefixed or unsupported-prefix path yields `localeCode: null` so the
 * caller can decide whether to negotiate-and-redirect or render a 404.
 */
export function splitLocalePath(pathname) {
    const [, firstSegment = '', ...rest] = String(pathname || '/').split('/');
    const parsed = parseLocale(firstSegment);

    if (!parsed) {
        return { localeCode: null, routePath: String(pathname || '/') || '/' };
    }

    return { localeCode: parsed.localeCode, routePath: `/${rest.join('/')}` };
}

/**
 * Drop a first segment that has the SHAPE of a locale, whether or not we ship
 * it.
 *
 * This is what makes `/de/garage` land on `/en/garage` rather than `/en` — a
 * visitor following a link for a language we have not launched keeps the screen
 * they were promised. `splitLocalePath` cannot do this job: it only recognises
 * supported locales, so to it `de` is an ordinary path segment.
 *
 * The guard is that a route segment must not be mistaken for a language. Every
 * route this app owns is a word longer than two letters (`garage`, `cars`),
 * which `routes.test.js` holds it to — the day a two-letter route is proposed,
 * that test fails rather than the router silently eating the segment.
 */
export function stripLocaleLikePrefix(pathname) {
    const normalizedPath = String(pathname || '/') || '/';
    const [, firstSegment = '', ...rest] = normalizedPath.split('/');

    if (!LOCALE_PATTERN.test(firstSegment.toLowerCase())) {
        return normalizedPath;
    }

    return `/${rest.join('/')}`;
}

/** Move a path to another locale, keeping the route beneath it. */
export function withLocale(pathname, localeCode) {
    const { routePath } = splitLocalePath(pathname);
    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');

    return `/${localeCode}${normalizedRoute}`;
}

export const FALLBACK_LOCALE = FALLBACK_LANGUAGE;
